import { useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface PlanIntakeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: PlanIntakeData) => Promise<void>;
  ideaTitle?: string;
}

export interface PlanIntakeData {
  hours_per_week: number;
  idea_type: string;
  current_state: string;
  constraint_text: string;
  done_definition: string;
  target_date: string | null;
}

const HOURS_OPTIONS = [
  { value: '3', label: '3 hrs' },
  { value: '5', label: '5 hrs' },
  { value: '10', label: '10 hrs' },
  { value: '20', label: '20 hrs' },
];

const IDEA_TYPES = [
  { value: 'digital', label: 'Digital' },
  { value: 'physical', label: 'Physical' },
  { value: 'service', label: 'Service' },
  { value: 'content', label: 'Content' },
];

const CURRENT_STATES = [
  { value: 'just_an_idea', label: 'Just an idea' },
  { value: 'some_research', label: 'Some research' },
  { value: 'already_started', label: 'Already started' },
  { value: 'restarting', label: 'Restarting' },
];

const CONSTRAINTS = [
  { value: 'time', label: 'Time' },
  { value: 'money', label: 'Money' },
  { value: 'skills', label: 'Skills' },
  { value: 'confidence', label: 'Confidence' },
  { value: 'access', label: 'Access' },
  { value: 'not_sure', label: 'Not sure' },
];

const DONE_DEFINITIONS = [
  { value: 'asap', label: 'ASAP' },
  { value: 'soft_date', label: 'Soft date' },
  { value: 'momentum', label: 'Momentum only' },
];

export function PlanIntakeModal({ open, onOpenChange, onSubmit, ideaTitle }: PlanIntakeModalProps) {
  const [loading, setLoading] = useState(false);
  const [hoursPerWeek, setHoursPerWeek] = useState<string>('5');
  const [ideaType, setIdeaType] = useState<string>('');
  const [currentState, setCurrentState] = useState<string>('');
  const [constraint, setConstraint] = useState<string>('');
  const [doneDefinition, setDoneDefinition] = useState<string>('');
  const [targetDate, setTargetDate] = useState<Date | undefined>();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    setLoading(true);
    try {
      await onSubmit({
        hours_per_week: parseInt(hoursPerWeek),
        idea_type: ideaType,
        current_state: currentState,
        constraint_text: constraint,
        done_definition: doneDefinition,
        target_date: doneDefinition === 'soft_date' && targetDate 
          ? targetDate.toISOString().split('T')[0] 
          : null,
      });
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const isValid = hoursPerWeek && ideaType && currentState && constraint && doneDefinition && 
    (doneDefinition !== 'soft_date' || targetDate);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create a Plan</DialogTitle>
          <DialogDescription>
            {ideaTitle ? `Plan for "${ideaTitle}"` : 'Answer a few questions to generate your plan'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 pt-2">
          {/* Hours per week */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Hours per week</Label>
            <RadioGroup
              value={hoursPerWeek}
              onValueChange={setHoursPerWeek}
              className="flex flex-wrap gap-2"
            >
              {HOURS_OPTIONS.map((option) => (
                <div key={option.value}>
                  <RadioGroupItem
                    value={option.value}
                    id={`hours-${option.value}`}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={`hours-${option.value}`}
                    className={cn(
                      "flex items-center justify-center px-4 py-2 rounded-md border cursor-pointer text-sm",
                      "peer-data-[state=checked]:bg-primary peer-data-[state=checked]:text-primary-foreground peer-data-[state=checked]:border-primary",
                      "hover:bg-muted/50 transition-colors",
                      hoursPerWeek === option.value && "bg-primary text-primary-foreground border-primary"
                    )}
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Idea type */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Idea type</Label>
            <RadioGroup
              value={ideaType}
              onValueChange={setIdeaType}
              className="flex flex-wrap gap-2"
            >
              {IDEA_TYPES.map((option) => (
                <div key={option.value}>
                  <RadioGroupItem
                    value={option.value}
                    id={`type-${option.value}`}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={`type-${option.value}`}
                    className={cn(
                      "flex items-center justify-center px-4 py-2 rounded-md border cursor-pointer text-sm",
                      "hover:bg-muted/50 transition-colors",
                      ideaType === option.value && "bg-primary text-primary-foreground border-primary"
                    )}
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Current state */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Current state</Label>
            <RadioGroup
              value={currentState}
              onValueChange={setCurrentState}
              className="flex flex-wrap gap-2"
            >
              {CURRENT_STATES.map((option) => (
                <div key={option.value}>
                  <RadioGroupItem
                    value={option.value}
                    id={`state-${option.value}`}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={`state-${option.value}`}
                    className={cn(
                      "flex items-center justify-center px-3 py-2 rounded-md border cursor-pointer text-sm",
                      "hover:bg-muted/50 transition-colors",
                      currentState === option.value && "bg-primary text-primary-foreground border-primary"
                    )}
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Biggest constraint */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Biggest constraint</Label>
            <RadioGroup
              value={constraint}
              onValueChange={setConstraint}
              className="flex flex-wrap gap-2"
            >
              {CONSTRAINTS.map((option) => (
                <div key={option.value}>
                  <RadioGroupItem
                    value={option.value}
                    id={`constraint-${option.value}`}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={`constraint-${option.value}`}
                    className={cn(
                      "flex items-center justify-center px-3 py-2 rounded-md border cursor-pointer text-sm",
                      "hover:bg-muted/50 transition-colors",
                      constraint === option.value && "bg-primary text-primary-foreground border-primary"
                    )}
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Definition of done */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Definition of done</Label>
            <RadioGroup
              value={doneDefinition}
              onValueChange={setDoneDefinition}
              className="flex flex-wrap gap-2"
            >
              {DONE_DEFINITIONS.map((option) => (
                <div key={option.value}>
                  <RadioGroupItem
                    value={option.value}
                    id={`done-${option.value}`}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={`done-${option.value}`}
                    className={cn(
                      "flex items-center justify-center px-4 py-2 rounded-md border cursor-pointer text-sm",
                      "hover:bg-muted/50 transition-colors",
                      doneDefinition === option.value && "bg-primary text-primary-foreground border-primary"
                    )}
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {/* Date picker when soft_date is selected */}
            {doneDefinition === 'soft_date' && (
              <div className="pt-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !targetDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {targetDate ? format(targetDate, "PPP") : "Pick a target date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={targetDate}
                      onSelect={setTargetDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={!isValid || loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate Plan'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}